import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Task, TaskStatus } from './types';
import { calculateNewPosition, mapSupabaseTaskToTask, mapTaskToSupabaseTask } from './taskUtils';
import { toast } from 'sonner';

const LOCAL_LIST_ID = 'local_list_default';
const LOCAL_TASKS_KEY = 'lovable_local_tasks';

export const useTaskOperations = (
  tasks: Task[],
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>,
  allTasks: Task[],
  setAllTasks: React.Dispatch<React.SetStateAction<Task[]>>,
  activeTask: Task | null,
  setActiveTask: React.Dispatch<React.SetStateAction<Task | null>>,
  session: any,
  activeListId: string | null,
  lists: any[]
) => {
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);

  const addTask = async (title: string, detail?: string) => {
    try {
      const listId = activeListId || lists[0]?.id;
      if (!listId) {
        toast.error("No active list found");
        return;
      }

      // Task limits are now checked in the UI before calling addTask
      // This keeps the API simple and moves limit enforcement to the UI layer

      const position = calculateNewPosition(tasks, 'to-do', listId);

      const newTask: Task = {
        id: crypto.randomUUID(),
        title,
        detail: detail || '',
        status: 'to-do' as TaskStatus,
        created_at: new Date().toISOString(),
        dateCreated: new Date(),
        list_id: listId,
        totalTimeSpent: 0,
        pomodorosCompleted: 0,
        is_active: false,
        position
      };

      if (session?.user) {
        newTask.user_id = session.user.id;

        const { error } = await supabase
          .from('tasks')
          .insert(mapTaskToSupabaseTask(newTask, session.user.id));

        if (error) throw error;
        
        setTasks(prev => [...prev, newTask]);
      } else {
        // For non-authenticated users, save to local storage
        const newTasks = [...tasks, newTask];
        setTasks(newTasks);
        
        // Save to localStorage
        localStorage.setItem(LOCAL_TASKS_KEY, JSON.stringify(newTasks));
      }
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error("Failed to add task");
    }
  };

  const updateTask = async (updatedTask: Task) => {
    try {
      const updatedTasks = tasks.map(task => 
        task.id === updatedTask.id ? { ...task, ...updatedTask } : task
      );
      
      setTasks(updatedTasks);
      
      const updatedAllTasks = allTasks.map(task => 
        task.id === updatedTask.id ? { ...task, ...updatedTask } : task
      );
      setAllTasks(updatedAllTasks);
      
      if (activeTask && activeTask.id === updatedTask.id) {
        setActiveTask({ ...activeTask, ...updatedTask });
      }
      
      if (session?.user) {
        const { error } = await supabase
          .from('tasks')
          .update(mapTaskToSupabaseTask(updatedTask, session.user.id))
          .eq('id', updatedTask.id)
          .eq('user_id', session.user.id);

        if (error) throw error;
      } else {
        // For non-authenticated users, save to local storage
        localStorage.setItem(LOCAL_TASKS_KEY, JSON.stringify(updatedTasks));
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (id: string) => {
    try {
      const updatedTasks = tasks.filter(task => task.id !== id);
      setTasks(updatedTasks);
      
      const updatedAllTasks = allTasks.filter(task => task.id !== id);
      setAllTasks(updatedAllTasks);
      
      if (activeTask && activeTask.id === id) {
        setActiveTask(null);
      }
      
      if (session?.user) {
        const { error } = await supabase
          .from('tasks')
          .delete()
          .eq('id', id)
          .eq('user_id', session.user.id);

        if (error) throw error;
      } else {
        // For non-authenticated users, save to local storage
        localStorage.setItem(LOCAL_TASKS_KEY, JSON.stringify(updatedTasks));
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error("Failed to delete task");
    }
  };

  const updateTaskTime = (seconds: number, incrementPomodoro = false) => {
    if (!activeTask) return;
    
    const updatedTask = {
      ...activeTask,
      totalTimeSpent: activeTask.totalTimeSpent + seconds,
      pomodorosCompleted: incrementPomodoro 
        ? activeTask.pomodorosCompleted + 1 
        : activeTask.pomodorosCompleted
    };
    
    updateTask(updatedTask);
  };

  const reorderTasks = async (taskId: string, sourceIndex: number, destinationIndex: number, status: TaskStatus) => {
    try {
      const statusTasks = tasks
        .filter(task => task.status === status)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      
      const taskToMove = statusTasks.find(task => task.id === taskId);
      if (!taskToMove) return;

      const reorderedTasks = [...statusTasks];
      reorderedTasks.splice(sourceIndex, 1);
      reorderedTasks.splice(destinationIndex, 0, taskToMove);
      
      const updatedStatusTasks = reorderedTasks.map((task, index) => ({
        ...task,
        position: index
      }));
      
      const updatedTasks = tasks.map(task => {
        const updatedTask = updatedStatusTasks.find(ut => ut.id === task.id);
        return updatedTask || task;
      });
      
      setTasks(updatedTasks);
      
      if (session?.user) {
        const updates = updatedStatusTasks.map(task =>
          supabase
            .from('tasks')
            .update({ position: task.position })
            .eq('id', task.id)
            .eq('user_id', session.user.id)
        );

        await Promise.all(updates);
      } else {
        localStorage.setItem(LOCAL_TASKS_KEY, JSON.stringify(updatedTasks));
      }
    } catch (error) {
      console.error('Error reordering tasks:', error);
      toast.error("Failed to reorder tasks");
    }
  };

  const updateTaskStatus = async (id: string, newStatus: TaskStatus, newPosition?: number) => {
    try {
      const taskToUpdate = tasks.find(task => task.id === id);
      if (!taskToUpdate) return;

      const tasksWithNewStatus = tasks
        .filter(task => task.status === newStatus && task.id !== id)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      
      const position = newPosition !== undefined ? newPosition : tasksWithNewStatus.length;
      
      const updatedTask = { 
        ...taskToUpdate, 
        status: newStatus, 
        position 
      };

      const updatedTasks = tasks.map(task => 
        task.id === id ? updatedTask : task
      );
      setTasks(updatedTasks);

      if (activeTask && activeTask.id === id) {
        setActiveTask(updatedTask);
      }

      if (session?.user) {
        const { error } = await supabase
          .from('tasks')
          .update(mapTaskToSupabaseTask(updatedTask, session.user.id))
          .eq('id', id)
          .eq('user_id', session.user.id);

        if (error) throw error;
      } else {
        localStorage.setItem(LOCAL_TASKS_KEY, JSON.stringify(updatedTasks));
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error("Failed to update task status");
    }
  };

  return {
    addTask,
    updateTask,
    deleteTask,
    updateTaskTime,
    reorderTasks,
    updateTaskStatus,
    taskToEdit,
    setTaskToEdit
  };
};